import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Toast from '../../components/ui/Toast';
import ContentTypeBadge from '../../components/ui/ContentTypeBadge';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import StatsCard from './components/StatsCard';
import DraftCard from './components/DraftCard';
import PublishedArticleCard from './components/PublishedArticleCard';
import EngagementChart from './components/EngagementChart';
import CircleDistribution from './components/CircleDistribution';
import CollaborationCard from './components/CollaborationCard';
import NotificationItem from './components/NotificationItem';
import dashboardService from '../../services/dashboardService';
import articlesService from '../../services/articlesService';
import notificationsService from '../../services/notificationsService';
import circlesService from '../../services/circlesService';
import { getFilterableCircles, getSlugsForCircle } from '../../utils/circles';
import { toArabicDigits, withArabicDigits, formatDateYYYYMMDD } from '../../utils/numbers';

const WriterDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'overview';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);
  const [selectedCircle, setSelectedCircle] = useState('all');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [selectedTimeRange, setSelectedTimeRange] = useState('month');

  // Data state
  const [stats, setStats] = useState([]);
  const [circleDistribution, setCircleDistribution] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [publishedArticles, setPublishedArticles] = useState([]);
  const [collaborations, setCollaborations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [pendingArticles, setPendingArticles] = useState([]);
  const [circles, setCircles] = useState([]);

  // Loading state per tab / section
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingCircles, setLoadingCircles] = useState(true);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [loadingPublished, setLoadingPublished] = useState(true);
  const [loadingCollaborations, setLoadingCollaborations] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);

  // ---------- Fetchers ----------

  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const { data } = await dashboardService.getStats(selectedTimeRange);
      setStats([
        {
          icon: 'Eye',
          label: '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0645\u0634\u0627\u0647\u062f\u0627\u062a',
          value: data.totalViews?.value,
          trend: data.totalViews?.trend,
          trendValue: data.totalViews?.trendValue,
          color: 'primary',
        },
        {
          icon: 'Heart',
          label: '\u0627\u0644\u0625\u0639\u062c\u0627\u0628\u0627\u062a',
          value: data.totalClaps?.value,
          trend: data.totalClaps?.trend,
          trendValue: data.totalClaps?.trendValue,
          color: 'accent',
        },
        {
          icon: 'MessageCircle',
          label: '\u0627\u0644\u062a\u0639\u0644\u064a\u0642\u0627\u062a',
          value: data.totalComments?.value,
          trend: data.totalComments?.trend,
          trendValue: data.totalComments?.trendValue,
          color: 'success',
        },
        {
          icon: 'Users',
          label: '\u0627\u0644\u0645\u062a\u0627\u0628\u0639\u0648\u0646',
          value: data.followerCount?.value,
          trend: data.followerCount?.trend,
          trendValue: data.followerCount?.trendValue,
          color: 'warning',
        },
      ]);
    } catch {
      setStats([]);
    } finally {
      setLoadingStats(false);
    }
  }, [selectedTimeRange]);

  const fetchCircleDistribution = useCallback(async () => {
    setLoadingCircles(true);
    try {
      const { data } = await dashboardService.getCircleDistribution();
      setCircleDistribution(Array.isArray(data) ? data : []);
    } catch {
      setCircleDistribution([]);
    } finally {
      setLoadingCircles(false);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    try {
      const { data } = await articlesService.getMyDrafts();
      setDrafts(Array.isArray(data) ? data : []);
    } catch {
      setDrafts([]);
    } finally {
      setLoadingDrafts(false);
    }
  }, []);

  const fetchPublished = useCallback(async () => {
    setLoadingPublished(true);
    try {
      const { data } = await articlesService.getMyPublished();
      setPublishedArticles(Array.isArray(data) ? data : []);
    } catch {
      setPublishedArticles([]);
    } finally {
      setLoadingPublished(false);
    }
  }, []);

  const fetchCollaborations = useCallback(async () => {
    setLoadingCollaborations(true);
    try {
      const { data } = await dashboardService.getCollaborations();
      setCollaborations(Array.isArray(data) ? data : []);
    } catch {
      setCollaborations([]);
    } finally {
      setLoadingCollaborations(false);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const { data } = await notificationsService.getNotifications();
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const fetchPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const { data } = await articlesService.getMyPending();
      setPendingArticles(Array.isArray(data) ? data : []);
    } catch {
      setPendingArticles([]);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const fetchCircles = useCallback(async () => {
    try {
      const { data } = await circlesService.getCircles();
      setCircles(Array.isArray(data) ? data : []);
    } catch {
      setCircles([]);
    }
  }, []);

  // ---------- Effects: fetch on tab change / time range change ----------

  // Overview + Analytics both need stats, circles, and published
  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'analytics') {
      fetchStats();
      fetchCircleDistribution();
      fetchPublished();
    }
  }, [activeTab, fetchStats, fetchCircleDistribution, fetchPublished]);

  useEffect(() => {
    if (activeTab === 'drafts') {
      fetchDrafts();
      fetchCircles();
    }
  }, [activeTab, fetchDrafts, fetchCircles]);

  useEffect(() => {
    if (activeTab === 'published') {
      fetchPublished();
      fetchCircles();
    }
  }, [activeTab, fetchPublished, fetchCircles]);

  useEffect(() => {
    if (activeTab === 'collaborations') {
      fetchCollaborations();
    }
  }, [activeTab, fetchCollaborations]);

  useEffect(() => {
    if (activeTab === 'notifications') {
      fetchNotifications();
    }
  }, [activeTab, fetchNotifications]);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending();
    }
  }, [activeTab, fetchPending]);

  // ---------- Dynamic option lists (from API - single source of truth) ----------
  // Only show root circles in filter (دائرة الأدب، دائرة الشعور، etc.) - NOT poetry subcategories (شعرٌ فصيح، شعرٌ حديث، شعرٌ عامّي)
  const filterableCircles = getFilterableCircles(circles);
  const circleOptions = [
    { value: 'all', label: 'جميع الدوائر' },
    ...filterableCircles.map((c) => ({ value: c.slug, label: c.name })),
  ];

  const slugsForFilter = getSlugsForCircle(circles, selectedCircle);
  const filteredDrafts =
    selectedCircle === 'all'
      ? drafts
      : drafts.filter((d) =>
          d.circleSlug ? slugsForFilter.includes(d.circleSlug) : slugsForFilter.some((s) => circles.find((c) => c.slug === s)?.name === d.circle)
        );
  const filteredPublished =
    selectedCircle === 'all'
      ? publishedArticles
      : publishedArticles.filter((a) =>
          a.circleSlug ? slugsForFilter.includes(a.circleSlug) : slugsForFilter.some((s) => circles.find((c) => c.slug === s)?.name === a.circle)
        );

  const timeRangeOptions = [
    { value: 'week', label: '\u0622\u062e\u0631 \u0623\u0633\u0628\u0648\u0639' },
    { value: 'month', label: '\u0622\u062e\u0631 \u0634\u0647\u0631' },
    { value: 'quarter', label: '\u0622\u062e\u0631 \u0663 \u0623\u0634\u0647\u0631' },
    { value: 'year', label: '\u0622\u062e\u0631 \u0633\u0646\u0629' },
  ];

  const tabs = [
    { id: 'overview', label: '\u0646\u0638\u0631\u0629 \u0639\u0627\u0645\u0629', icon: 'LayoutDashboard' },
    { id: 'drafts', label: '\u0627\u0644\u0645\u0633\u0648\u062f\u0627\u062a', icon: 'FileEdit' },
    { id: 'pending', label: 'قيد المراجعة', icon: 'Clock' },
    { id: 'published', label: '\u0627\u0644\u0645\u0646\u0634\u0648\u0631\u0627\u062a', icon: 'FileCheck' },
    { id: 'analytics', label: '\u0627\u0644\u062a\u062d\u0644\u064a\u0644\u0627\u062a', icon: 'BarChart3' },
    { id: 'collaborations', label: '\u0627\u0644\u062a\u0639\u0627\u0648\u0646', icon: 'Users' },
    { id: 'notifications', label: '\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a', icon: 'Bell' },
  ];

  // ---------- Handlers ----------

  const handleEditDraft = (draftId) => {
    navigate(`/creator-studio/${draftId}`);
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      await articlesService.deleteArticle(draftId);
      setDrafts((prev) => prev.filter((d) => d.id !== draftId));
    } catch {
      // Could surface a toast here in the future
    }
  };

  const handlePublishDraft = async (draft) => {
    if (!draft?.id) return;
    const type = (draft.type || draft.Type || 'Article').toLowerCase();
    const isArticle = type === 'article';
    if (isArticle && !draft?.coverImage) {
      setToastMessage('الرجاء إضافة صورة غلاف للمقال');
      setToastVariant('error');
      setShowToast(true);
      return;
    }
    try {
      await articlesService.publishArticle(draft.id);
      setDrafts((prev) => prev.filter((d) => d.id !== draft.id));
      fetchPending();
    } catch (err) {
      const data = err?.response?.data;
      const msg = (data?.code === 'MISSING_COVER' || data?.message?.includes('غلاف'))
        ? 'الرجاء إضافة صورة غلاف للمقال'
        : (data?.message || 'فشل إرسال المقال للمراجعة');
      setToastMessage(msg);
      setToastVariant('error');
      setShowToast(true);
    }
  };

  const handleViewArticle = (articleId) => {
    navigate(`/article/${articleId}`);
  };

  const handleEditArticle = (articleId) => {
    navigate(`/creator-studio/${articleId}`);
  };

  const handleUnpublishArticle = async (articleId) => {
    try {
      await articlesService.unpublishArticle(articleId);
      setPublishedArticles((prev) => prev.filter((a) => a.id !== articleId));
      fetchDrafts();
    } catch {
      // Could surface a toast here in the future
    }
  };

  const handleAcceptCollaboration = (collaborationId) => {
    console.log('Accept collaboration:', collaborationId);
  };

  const handleDeclineCollaboration = (collaborationId) => {
    console.log('Decline collaboration:', collaborationId);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch {
      // silent
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch {
      // silent
    }
  };

  const unreadNotifications = notifications?.filter((n) => !n?.isRead)?.length;

  // ---------- Loading skeletons ----------

  const StatsSkeletons = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-card rounded-lg p-4 md:p-6 shadow-warm animate-pulse">
          <div className="flex items-start justify-between mb-3 md:mb-4">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-muted" />
            <div className="w-16 h-4 rounded bg-muted" />
          </div>
          <div className="space-y-2">
            <div className="w-24 h-8 rounded bg-muted" />
            <div className="w-32 h-4 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );

  const ChartSkeleton = () => (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm animate-pulse">
      <div className="w-40 h-6 rounded bg-muted mb-6" />
      <div className="w-full h-48 md:h-64 rounded bg-muted" />
    </div>
  );

  const CardListSkeleton = ({ count = 3 }) => (
    <div className="space-y-4">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="bg-card rounded-lg p-4 md:p-5 shadow-warm animate-pulse">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full md:w-32 lg:w-40 h-40 md:h-24 lg:h-28 rounded-lg bg-muted" />
            <div className="flex-1 space-y-3">
              <div className="w-3/4 h-6 rounded bg-muted" />
              <div className="w-full h-4 rounded bg-muted" />
              <div className="flex gap-3">
                <div className="w-16 h-6 rounded-full bg-muted" />
                <div className="w-24 h-4 rounded bg-muted" />
                <div className="w-20 h-4 rounded bg-muted" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const GridCardSkeleton = ({ count = 3 }) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="bg-card rounded-lg overflow-hidden shadow-warm animate-pulse">
          <div className="w-full h-40 md:h-48 lg:h-56 bg-muted" />
          <div className="p-4 md:p-5 lg:p-6 space-y-3">
            <div className="w-16 h-6 rounded-full bg-muted" />
            <div className="w-3/4 h-6 rounded bg-muted" />
            <div className="w-full h-4 rounded bg-muted" />
            <div className="flex gap-3">
              <div className="w-12 h-4 rounded bg-muted" />
              <div className="w-12 h-4 rounded bg-muted" />
              <div className="w-12 h-4 rounded bg-muted" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const NotificationsSkeleton = ({ count = 5 }) => (
    <div className="bg-card rounded-lg shadow-warm divide-y divide-border">
      {[...Array(count)].map((_, i) => (
        <div key={i} className="flex items-start gap-3 p-3 md:p-4 animate-pulse">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-muted flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="w-3/4 h-4 rounded bg-muted" />
            <div className="w-24 h-3 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );

  // ---------- Render ----------

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-36 lg:pt-40 pb-12 md:pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-6 md:mb-8 lg:mb-10">
            <div className="mb-4 md:mb-6">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                لوحة الكاتب
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                إدارة محتواك وتتبع تأثيرك الثقافي
              </p>
            </div>

            {/* Tabs Navigation */}
            <div className="bg-card rounded-lg p-2 shadow-warm overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap ${activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground shadow-warm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}>
                    <Icon name={tab?.icon} size={18} className="md:w-5 md:h-5" />
                    <span>{tab?.label}</span>
                    {tab?.id === 'notifications' && unreadNotifications > 0 && (
                      <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">
                        {unreadNotifications}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6 md:space-y-8">
              {/* Time range selector */}
              <div className="flex justify-end">
                <Select
                  options={timeRangeOptions}
                  value={selectedTimeRange}
                  onChange={setSelectedTimeRange}
                  placeholder="الفترة الزمنية"
                  className="w-full sm:w-64"
                />
              </div>

              {/* Stats Grid */}
              {loadingStats ? (
                <StatsSkeletons />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {stats?.map((stat, index) => (
                    <StatsCard key={index} {...stat} />
                  ))}
                </div>
              )}

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                {loadingStats ? <ChartSkeleton /> : <EngagementChart stats={stats} />}
                {loadingCircles ? <ChartSkeleton /> : <CircleDistribution data={circleDistribution} />}
              </div>

              {/* Recent Activity */}
              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
                  النشاط الأخير
                </h3>
                {loadingPublished ? (
                  <CardListSkeleton count={3} />
                ) : (
                  <div className="space-y-3 md:space-y-4">
                    {publishedArticles?.slice(0, 3)?.map((article) => (
                      <PublishedArticleCard
                        key={article?.id}
                        article={article}
                        onView={handleViewArticle}
                        onEdit={handleEditArticle}
                      />
                    ))}
                    {publishedArticles?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        لا توجد مقالات منشورة بعد
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drafts Tab */}
          {activeTab === 'drafts' && (
            <div className="space-y-4 md:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  المسودات ({toArabicDigits(filteredDrafts?.length ?? 0)})
                </h2>
                <Select
                  options={circleOptions}
                  value={selectedCircle}
                  onChange={setSelectedCircle}
                  placeholder="تصفية حسب الدائرة"
                  className="w-full sm:w-64"
                />
              </div>
              {loadingDrafts ? (
                <CardListSkeleton count={3} />
              ) : filteredDrafts?.length > 0 ? (
                <div className="space-y-4">
                  {filteredDrafts?.map((draft) => (
                    <DraftCard
                      key={draft?.id}
                      draft={draft}
                      onEdit={handleEditDraft}
                      onDelete={handleDeleteDraft}
                      onPublish={handlePublishDraft}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-8 md:p-12 shadow-warm text-center">
                  <Icon name="FileEdit" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">لا توجد مسودات بعد</p>
                </div>
              )}
            </div>
          )}

          {/* Pending Review Tab */}
          {activeTab === 'pending' && (
            <div className="space-y-4 md:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  قيد المراجعة ({toArabicDigits(pendingArticles?.length ?? 0)})
                </h2>
              </div>
              {loadingPending ? (
                <CardListSkeleton count={3} />
              ) : pendingArticles?.length > 0 ? (
                <div className="space-y-4">
                  {pendingArticles.map((article) => {
                    const statusConfig = {
                      PendingReview: { label: 'في الانتظار', color: 'bg-yellow-500', icon: 'Clock' },
                      Revision: { label: 'مطلوب تعديل', color: 'bg-orange-500', icon: 'Edit' },
                      Rejected: { label: 'مرفوض', color: 'bg-red-500', icon: 'XCircle' },
                    };
                    const sc = statusConfig[article.status] || statusConfig.PendingReview;
                    return (
                      <div
                        key={article.id}
                        className="bg-card rounded-lg p-4 md:p-5 shadow-warm"
                      >
                        <div className="flex flex-col md:flex-row gap-4">
                          {article.coverImage && (
                            <img
                              src={article.coverImage}
                              alt={article.coverImageAlt || article.title}
                              className="w-full md:w-32 lg:w-40 h-40 md:h-24 lg:h-28 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="flex flex-wrap items-center gap-2 min-w-0">
                                <ContentTypeBadge type={article.type} size="sm" />
                                <h3 className="font-heading font-semibold text-foreground text-base md:text-lg">
                                  {article.title}
                                </h3>
                              </div>
                              <span className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium text-white ${sc.color}`}>
                                <Icon name={sc.icon} size={12} className="inline ml-1" />
                                {sc.label}
                              </span>
                            </div>
                            {article.excerpt && (
                              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{article.excerpt}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span>{article.circle}</span>
                              {article.submittedAt && <span>أُرسل: <span dir="ltr">{formatDateYYYYMMDD(article.submittedAt)}</span></span>}
                            </div>
                            {article.reviewNote && (
                              <div className="mt-3 p-3 bg-warning/10 rounded-lg border border-warning/30">
                                <p className="text-xs font-medium text-foreground mb-1">ملاحظات المراجع:</p>
                                <p className="text-xs text-muted-foreground">{article.reviewNote}</p>
                              </div>
                            )}
                            {(article.status === 'Revision' || article.status === 'Rejected') && (
                              <div className="mt-3 flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/creator-studio/${article.id}`)}
                                  className="text-xs"
                                >
                                  <Icon name="Edit" size={14} className="ml-1" />
                                  تعديل وإعادة إرسال
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-8 md:p-12 shadow-warm text-center">
                  <Icon name="CheckCircle" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">لا توجد مقالات قيد المراجعة</p>
                </div>
              )}
            </div>
          )}

          {/* Published Tab */}
          {activeTab === 'published' && (
            <div className="space-y-4 md:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  المقالات المنشورة ({toArabicDigits(filteredPublished?.length ?? 0)})
                </h2>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Select
                    options={circleOptions}
                    value={selectedCircle}
                    onChange={setSelectedCircle}
                    placeholder="تصفية حسب الدائرة"
                    className="w-full sm:w-48"
                  />
                  <Select
                    options={timeRangeOptions}
                    value={selectedTimeRange}
                    onChange={setSelectedTimeRange}
                    placeholder="الفترة الزمنية"
                    className="w-full sm:w-48"
                  />
                </div>
              </div>
              {loadingPublished ? (
                <GridCardSkeleton count={3} />
              ) : filteredPublished?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredPublished?.map((article) => (
                    <PublishedArticleCard
                      key={article?.id}
                      article={article}
                      onView={handleViewArticle}
                      onEdit={handleEditArticle}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-8 md:p-12 shadow-warm text-center">
                  <Icon name="FileCheck" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">لا توجد مقالات منشورة بعد</p>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6 md:space-y-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  تحليلات الأداء
                </h2>
                <Select
                  options={timeRangeOptions}
                  value={selectedTimeRange}
                  onChange={setSelectedTimeRange}
                  placeholder="الفترة الزمنية"
                  className="w-full sm:w-64"
                />
              </div>

              {loadingStats ? (
                <StatsSkeletons />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                  {stats?.map((stat, index) => (
                    <StatsCard key={index} {...stat} />
                  ))}
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                {loadingStats ? <ChartSkeleton /> : <EngagementChart stats={stats} />}
                {loadingCircles ? <ChartSkeleton /> : <CircleDistribution data={circleDistribution} />}
              </div>

              <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground mb-4 md:mb-6">
                  أفضل المقالات أداءًا
                </h3>
                {loadingPublished ? (
                  <CardListSkeleton count={3} />
                ) : (
                  <div className="space-y-3 md:space-y-4">
                    {[...(publishedArticles ?? [])]
                      .sort((a, b) => {
                        const scoreA = (a?.views ?? 0) + (a?.claps ?? 0) + (a?.comments ?? 0);
                        const scoreB = (b?.views ?? 0) + (b?.claps ?? 0) + (b?.comments ?? 0);
                        return scoreB - scoreA;
                      })
                      .slice(0, 5)
                      .map((article) => (
                      <div
                        key={article?.id}
                        className="flex items-center justify-between p-3 md:p-4 bg-muted/50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm md:text-base font-medium text-foreground mb-1 line-clamp-1">
                            {article?.title}
                          </h4>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Icon name="Eye" size={12} />
                              {toArabicDigits(article?.views)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Heart" size={12} />
                              {toArabicDigits(article?.claps)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="MessageCircle" size={12} />
                              {toArabicDigits(article?.comments)}
                            </span>
                          </div>
                        </div>
                      </div>
                      ))}
                    {publishedArticles?.length === 0 && (
                      <p className="text-center text-muted-foreground py-8">
                        لا توجد مقالات منشورة بعد
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Collaborations Tab */}
          {activeTab === 'collaborations' && (
            <div className="space-y-4 md:space-y-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                المشاريع التعاونية
              </h2>
              {loadingCollaborations ? (
                <GridCardSkeleton count={3} />
              ) : collaborations?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {collaborations?.map((collaboration) => (
                    <CollaborationCard
                      key={collaboration?.id}
                      collaboration={collaboration}
                      onAccept={handleAcceptCollaboration}
                      onDecline={handleDeclineCollaboration}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-8 md:p-12 shadow-warm text-center">
                  <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">لا توجد مشاريع تعاونية حاليًا</p>
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-4 md:space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-bold text-foreground">
                  الإشعارات
                </h2>
                {unreadNotifications > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMarkAllAsRead}>
                    تحديد الكل كمقروء
                  </Button>
                )}
              </div>
              {loadingNotifications ? (
                <NotificationsSkeleton count={5} />
              ) : notifications?.length > 0 ? (
                <div className="bg-card rounded-lg shadow-warm divide-y divide-border">
                  {notifications?.map((notification) => (
                    <NotificationItem
                      key={notification?.id}
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg p-8 md:p-12 shadow-warm text-center">
                  <Icon name="Bell" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">لا توجد إشعارات</p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      {showToast && (
        <Toast message={toastMessage} onClose={() => setShowToast(false)} duration={3000} variant={toastVariant} />
      )}
    </div>
  );
};

export default WriterDashboard;
